import { ChevronRightIcon } from "@heroicons/react/solid";
import axios from "axios";
import { useEffect, useState } from "react";
import { FaPlusSquare, FaSpinner, FaUserShield } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import stringToColor from "string-to-color";
import api from "../api";
import { ITeam } from "../api/services/team.service";
import { useAuth } from "../hooks/useAuth";
import { usePagination } from "../hooks/usePagination";
import Pagination from "./shared/Pagination";
import TextEditDialog from "./shared/TextEditDialog";

export default function TeamsListSection({ pageSize }: { pageSize?: number }) {
  let navigate = useNavigate();
  const { user } = useAuth();

  const [teams, setTeams] = useState<ITeam[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [isTeamAddDialogOpen, setIsTeamAddDialogOpen] = useState(false);

  const { page, paginationProps, searchTerm, updatePagination, setPage } =
    usePagination();

  useEffect(() => {
    searchTeams(searchTerm, page);
  }, [searchTerm, page]);

  async function searchTeams(searchTerm: string | null, page: number | null) {
    try {
      setLoading(true);
      const { data } = await api.team.searchTeams({
        term: searchTerm,
        page,
        size: pageSize || 10,
      });
      if (data.result) {
        const teamPageContent = data.result.content;
        setTeams(teamPageContent);
        updatePagination(data);
        setLoading(false);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  async function addTeam({ name }: { name: string }) {
    try {
      if (user) {
        const { data } = await api.team.addTeam({
          name,
          userId: user.id,
        });
        if (data.result) {
          navigate(`/team/${data.result.id}`);
        }
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  function TeamRow({ team }: { team: ITeam }) {
    return (
      <div
        className="break-all py-4 px-4 cursor-pointer"
        onClick={() => navigate(`/team/${team.id}`)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0 mr-3 break-all">
              <FaUserShield
                className="bg-white rounded-full h-10 w-10"
                color={stringToColor(team.id)}
              />
            </div>
            <div className="break-all">
              <p className="text-sm font-medium text-red-600 truncate">
                {team.name}
              </p>
            </div>
          </div>

          <div className="flex">
            <div className="hidden md:flex mr-10">10</div>
            <ChevronRightIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col rounded-2xl shadow-xl bg-white grow">
      <TextEditDialog
        isOpen={isTeamAddDialogOpen}
        title={"New Team"}
        placeholder={"New Team"}
        onCancel={() => {
          setIsTeamAddDialogOpen(false);
        }}
        onSubmit={(newValue: string) => {
          addTeam({ name: newValue });
          setIsTeamAddDialogOpen(false);
        }}
      />
      <div className="flex items-center p-4 w-full border-b justify-between">
        <div className="flex items-center">
          <div className="flex items-center mr-10 rounded-lg">
            <div className="">
              <FaUserShield size={20} className="text-red-700" />
            </div>
            <span className="ml-2 font-medium leading-6 text-slate-700 text-lg flex ">
              Teams
            </span>
          </div>
        </div>
        <div className="flex space-x-2">
          <div className="font-semibold text-slate-700">{teams.length}</div>
          <div
            className="tour-1-step1 cursor-pointer text-indigo-600"
            onClick={() => {
              setIsTeamAddDialogOpen(true);
            }}
          >
            <FaPlusSquare size={24} />
          </div>
        </div>
      </div>
      {loading && (
        <div className="flex items-center justify-center p-20 text-slate-500">
          <FaSpinner size={30} className="animate-spin" />
        </div>
      )}
      {!loading && teams.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-200 w-full overflow-scroll overflow-x-hidden ">
          {teams.map((team, i) => (
            <TeamRow key={i} team={team} />
          ))}
        </div>
      )}
      {!loading && teams.length === 0 && (
        <div className="flex flex-col items-center justify-center p-8 text-slate-500">
          <div className="font-semibold text-slate-700 mb-2">
            No Teams found
          </div>
          <div className="inline-flex items-center space-x-2">
            <div>Maybe try different search parameters</div>
          </div>
        </div>
      )}
      <Pagination {...paginationProps} goToPage={setPage} />
    </div>
  );
}
